import React, { useEffect } from 'react';
import { Form, Input, InputNumber, Button, Select, Card, message, Row, Col, Divider } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { updateOrder } from '../store/ordersSlice';
import { fetchProducts } from '../store/productsSlice';
import { fetchShops } from '../store/shopsSlice';
import { fetchBDEs } from '../store/bdeSlice';
import { formatName } from '../utils/formatters';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const { Option } = Select;

const OrderEdit = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { orders } = useSelector((state) => state.orders);
  const { products } = useSelector((state) => state.products);
  const { shops } = useSelector((state) => state.shops);
  const { users } = useSelector((state) => state.bde);

  // Watch items to calculate totals
  const items = Form.useWatch('items', form);

  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchShops());
    dispatch(fetchBDEs());
  }, [dispatch]);

  useEffect(() => {
    const order = orders.find((o) => o.id === orderId);
    if (order) {
      // Ensure items is an array
      const initialItems = order.items && order.items.length > 0 ? order.items : [{
        productName: order.productName,
        quantity: order.quantity,
        mrp: order.mrp,
        amount: order.billingPrice || order.amount
      }];

      // Find shop and BDE names
      const shop = shops.find(s => s.id === order.shopId);
      const bde = users.find(u => u.id === order.bdeId);

      form.setFieldsValue({
        ...order,
        items: initialItems,
        shopName: shop ? `${shop.name} (${shop.pincode})` : order.shopId,
        bdeName: bde ? bde.name : order.bdeId
      });
    } else {
      // message.error('Order not found'); // Removed to prevent double toast on reload
      // navigate('/admin/orders');
    }
  }, [orderId, orders, form, shops, users]);

  // Auto-calculate totals when items change
  useEffect(() => {
    if (items && items.length > 0) {
      const totalAmount = Number(
        items.reduce((sum, item) => sum + (Number(item?.amount) || 0), 0).toFixed(2)
      );
      form.setFieldsValue({ amount: totalAmount });
    }
  }, [items, form]);

  const onFinish = (values) => {
    const formattedValues = {
      ...values,
      customerName: formatName(values.customerName),
      items: values.items.map(item => ({
        ...item,
        productName: item.productName,
        quantity: Number(item.quantity) || 0,
        mrp: Number(item.mrp) || 0,
        amount: Number((Number(item.amount) || 0).toFixed(2))
      }))
    };

    // Keep original fields that are not in the form
    const originalOrder = orders.find((o) => o.id === orderId);

    // Construct payload
    // We use FormData if we want to support file upload in future, but for now JSON is fine unless paymentProof is edited
    // The existing updateOrder handles FormData or Object.
    // If we are not uploading a new file, we can just send the object.

    const updatedOrder = {
      ...originalOrder,
      ...formattedValues,
      id: originalOrder.id // Ensure ID is present
    };

    dispatch(updateOrder(updatedOrder))
      .unwrap()
      .then(() => {
        message.success('Order updated successfully');
        navigate('/admin/orders');
      })
      .catch((err) => {
        message.error('Failed to update order: ' + (err.message || JSON.stringify(err)));
      });
  };

  const handleProductChange = (index, productName) => {
    const product = products.find(p => p.name === productName);
    if (product) {
      const currentQty = form.getFieldValue(['items', index, 'quantity']) || 1;
      const rate = product.discountedPrice || product.price;
      form.setFieldsValue({
        items: {
          [index]: {
            mrp: (product.price || 0) * currentQty,
            amount: Number(((rate || 0) * currentQty).toFixed(2))
          }
        }
      });
    }
  };

  const handleQuantityChange = (index, quantity) => {
    const productName = form.getFieldValue(['items', index, 'productName']);
    const product = products.find(p => p.name === productName);
    if (product) {
      const rate = product.discountedPrice || product.price;
      form.setFieldsValue({
        items: {
          [index]: {
            mrp: Number(((product.price || 0) * quantity).toFixed(2)),
            amount: Number(((rate || 0) * quantity).toFixed(2))
          }
        }
      });
    }
  };

  return (
    <Card title={`Edit Order: ${orderId}`}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="customerName"
              label="Customer Name"
              rules={[{ required: true, message: 'Please input customer name!' }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Please select status!' }]}
            >
              <Select>
                <Option value="Pending">Pending</Option>
                <Option value="Expected">Expected</Option>
                <Option value="Processing">Processing</Option>
                <Option value="Completed">Completed</Option>
                <Option value="Cancelled">Cancelled</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="shopName" label="Shop Name">
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="bdeName" label="BDE Name">
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="gstIn" label="GSTIN">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="paymentMethod" label="Payment Method">
              <Select>
                <Option value="CASH">Cash</Option>
                <Option value="ONLINE">Online</Option>
                <Option value="CASH ON DELIVERY">Cash on Delivery</Option>
                <Option value="PREPAID">Prepaid</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Order Items</Divider>

        <Form.List name="items">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, index) => (
                <Row key={key} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                  <Col span={8}>
                    <Form.Item
                      {...restField}
                      name={[name, 'productName']}
                      label={index === 0 ? "Product" : ""}
                      rules={[{ required: true, message: 'Missing product' }]}
                    >
                      <Select
                        showSearch
                        placeholder="Select Product"
                        onChange={(val) => handleProductChange(index, val)}
                      >
                        {products.map(p => (
                          <Option key={p._id} value={p.name}>
                            {p.name} (Stock: {p.stock})
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      {...restField}
                      name={[name, 'quantity']}
                      label={index === 0 ? "Qty" : ""}
                      rules={[{ required: true, message: 'Missing quantity' }]}
                    >
                      <InputNumber
                        min={1}
                        style={{ width: '100%' }}
                        onChange={(val) => handleQuantityChange(index, val)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      {...restField}
                      name={[name, 'mrp']}
                      label={index === 0 ? "MRP" : ""}
                    >
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      {...restField}
                      name={[name, 'amount']}
                      label={index === 0 ? "Amount" : ""}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        precision={2}
                        formatter={(value) => {
                          if (!value) return '';
                          return Number(value).toFixed(2);
                        }}
                        parser={(value) => parseFloat(value || 0)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={2}>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(name)}
                      style={{ marginTop: index === 0 ? 30 : 0 }}
                    />
                  </Col>
                </Row>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Product
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Divider />

        <Form.Item
          name="amount"
          label="Total Amount"
          rules={[{ required: true, message: 'Please input amount!' }]}
          extra={orders.find(o => o.id === orderId)?.commissionPaid ? "Amount cannot be changed after commission is paid." : null}
        >
          <InputNumber
            style={{ width: '100%' }}
            formatter={(value) => {
              if (!value) return '';
              const num = Number(value).toFixed(2);
              return `₹ ${num}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            }}
            parser={value => value.replace(/₹\s?|(,*)/g, '')}
            disabled={orders.find(o => o.id === orderId)?.commissionPaid}
          />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit">
            Update Order
          </Button>
          <Button style={{ marginLeft: 8 }} onClick={() => navigate('/admin/orders')}>
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default OrderEdit;
